import { useEffect, useState } from "react";
import { useGeneralContext } from "../context/AppGeneralContext";
import { COOKIE_TYPES } from "../enums/cookies";
import { generateUuid } from "../helpers/networkHelper";
import { evaluateFlag } from "../services/experiment.service";
import { gdprCookieHasValue } from "../utils/cookieUtils";
import { useLogger } from "./useLogger";

// The Amplitude flag must use local evaluation and exactly these variant keys: any other key (e.g. Amplitude's
// default `on`/`control`) falls back to the dropdowns without an exposure.
export const BOOK_SELECTION_FLAG = "book-selection-redesign";
/** Forces a variant in this browser, e.g. for QA and Cypress; forced views don't count as exposures. */
export const BOOK_SELECTION_VARIANT_OVERRIDE_KEY = "bookSelection.variant";
const ANONYMOUS_BUCKETING_ID_KEY = "experiment.anonymousId";

/** The old three dropdowns, or the four-step selection. */
export type BookSelectionVariant = "dropdowns" | "stepper";

const isBookSelectionVariant = (value: unknown): value is BookSelectionVariant =>
    value === "dropdowns" || value === "stepper";

/** Anonymous visitors have no account yet, so they are bucketed per browser instead. */
const getAnonymousBucketingId = (): string => {
    const id = localStorage.getItem(ANONYMOUS_BUCKETING_ID_KEY) ?? generateUuid();
    localStorage.setItem(ANONYMOUS_BUCKETING_ID_KEY, id);
    return id;
};

/** Known without asking Experiment: a forced variant, or the dropdowns for teachers who can't be measured. */
const getInitialVariant = (): BookSelectionVariant | undefined => {
    const override = localStorage.getItem(BOOK_SELECTION_VARIANT_OVERRIDE_KEY);
    if (isBookSelectionVariant(override)) {
        return override;
    }
    return gdprCookieHasValue(COOKIE_TYPES.ANALYTICS) ? undefined : "dropdowns";
};

/**
 * The book selection variant of the A/B test, per account (per browser for anonymous visitors).
 * Undefined while Experiment is asked; anything but a stepper assignment falls back to the dropdowns.
 */
export const useBookSelectionVariant = (): BookSelectionVariant | undefined => {
    const { userId } = useGeneralContext();
    const { logEvent } = useLogger();
    const [variant, setVariant] = useState(getInitialVariant);

    useEffect(() => {
        if (variant) {
            return;
        }

        let isCurrent = true;
        evaluateFlag(BOOK_SELECTION_FLAG, userId || getAnonymousBucketingId())
            .then((flagVariant) => {
                if (!isCurrent) {
                    return;
                }
                const assignedVariant = flagVariant?.value;
                if (isBookSelectionVariant(assignedVariant)) {
                    // Amplitude's exposure event: it sets the user property the variants are compared by.
                    logEvent("$exposure", { flag_key: BOOK_SELECTION_FLAG, variant: assignedVariant });
                }
                setVariant(assignedVariant === "stepper" ? "stepper" : "dropdowns");
            })
            .catch(() => {
                if (isCurrent) {
                    setVariant("dropdowns");
                }
            });

        return () => {
            isCurrent = false;
        };
        // Resolved once per visit, so the page never switches variants mid-selection.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return variant;
};
